import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { Database } from '../types/database.types'

type Chat = Database['public']['Tables']['chats']['Row'] & {
  athlete_profile?: {
    first_name: string | null
    last_name: string | null
    avatar_url: string | null
  }
  coach_profile?: {
    first_name: string | null
    last_name: string | null
    avatar_url: string | null
  }
  unread_count?: number // Count of unread messages for this chat
}

type ChatMessage = Database['public']['Tables']['chat_messages']['Row']

const MESSAGES_PER_PAGE = 15

interface ChatContextValue {
  // Chats
  chats: Chat[]
  chatsLoading: boolean
  chatsError: Error | null
  createChat: (athleteId: string, coachId: string) => Promise<Chat | null>
  createChatsForAthletes: (athleteIds: string[], coachId: string) => Promise<Chat[]>

  // Messages for active chat
  messages: ChatMessage[]
  messagesLoading: boolean
  messagesError: Error | null
  hasMore: boolean
  loadingMore: boolean
  sendMessage: (content: string, senderId: string, file?: File) => Promise<ChatMessage | null>
  markAsRead: (messageIds: string[]) => Promise<void>
  loadMoreMessages: () => Promise<void>

  // Active chat
  activeChatId: string | null
  setActiveChatId: (chatId: string | null) => void

  // Chat panel state
  isChatOpen: boolean
  setIsChatOpen: (isOpen: boolean) => void
  openChatWithAthlete: (athleteId: string) => void
}

const ChatContext = createContext<ChatContextValue | null>(null)

export const useChatContext = () => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider')
  }
  return context
}

interface ChatProviderProps {
  userId?: string
  children: React.ReactNode
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ userId, children }) => {
  // Chats state
  const [chats, setChats] = useState<Chat[]>([])
  const [chatsLoading, setChatsLoading] = useState(true)
  const [chatsError, setChatsError] = useState<Error | null>(null)

  // Messages state
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesError, setMessagesError] = useState<Error | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [oldestMessageId, setOldestMessageId] = useState<string | null>(null)

  // Active chat
  const [activeChatId, setActiveChatId] = useState<string | null>(null)

  // Chat panel state
  const [isChatOpen, setIsChatOpen] = useState(false)

  // Fetch chats
  useEffect(() => {
    if (!userId) {
      setChatsLoading(false)
      setChats([])
      return
    }

    const fetchChats = async () => {
      try {
        setChatsLoading(true)

        const { data, error: fetchError } = await supabase
          .from('chats')
          .select(`
            *,
            athlete_profile:profiles!athlete_id(first_name, last_name, avatar_url),
            coach_profile:profiles!coach_id(first_name, last_name, avatar_url)
          `)
          .or(`athlete_id.eq.${userId},coach_id.eq.${userId}`)
          .order('last_message_time', { ascending: false, nullsFirst: false })

        if (fetchError) {
          throw fetchError
        }

        // Fetch unread counts for all chats in a single query
        const chatIds = (data || []).map((chat) => chat.id)

        if (chatIds.length === 0) {
          setChats([])
          return
        }

        const { data: unreadCounts, error: countError } = await supabase
          .from('chat_messages')
          .select('chat_id')
          .in('chat_id', chatIds)
          .neq('sender_id', userId)
          .neq('status', 'read')

        if (countError) {
          console.error('Error fetching unread message counts:', countError)
          setChats((data || []).map((chat) => ({ ...chat, unread_count: 0 })))
          return
        }

        // Count messages per chat_id
        const unreadCountMap = (unreadCounts || []).reduce(
          (acc, msg) => {
            acc[msg.chat_id] = (acc[msg.chat_id] || 0) + 1
            return acc
          },
          {} as Record<string, number>
        )

        // Attach unread counts to chats
        const chatsWithUnreadCounts = (data || []).map((chat) => ({
          ...chat,
          unread_count: unreadCountMap[chat.id] || 0,
        }))

        setChats(chatsWithUnreadCounts)
      } catch (err) {
        console.error('Error fetching chats:', err)
        setChatsError(err as Error)
      } finally {
        setChatsLoading(false)
      }
    }

    fetchChats()

    // Set up real-time subscription for chat updates
    const subscription = supabase
      .channel('chats-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chats',
          filter: `athlete_id=eq.${userId}`,
        },
        () => {
          console.log('Chat inserted (as athlete)')
          fetchChats()
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chats',
          filter: `coach_id=eq.${userId}`,
        },
        () => {
          console.log('Chat inserted (as coach)')
          fetchChats()
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chats',
          filter: `athlete_id=eq.${userId}`,
        },
        (payload) => {
          console.log('Chat updated (as athlete):', payload)
          const updatedChat = payload.new as any
          setChats((prevChats) => {
            const updatedChats = prevChats.map((chat) =>
              chat.id === updatedChat.id ? { ...chat, ...updatedChat } : chat
            )
            return updatedChats.sort((a, b) => {
              const timeA = a.last_message_time || a.created_at
              const timeB = b.last_message_time || b.created_at
              return new Date(timeB).getTime() - new Date(timeA).getTime()
            })
          })
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chats',
          filter: `coach_id=eq.${userId}`,
        },
        (payload) => {
          console.log('Chat updated (as coach):', payload)
          const updatedChat = payload.new as any
          setChats((prevChats) => {
            const updatedChats = prevChats.map((chat) =>
              chat.id === updatedChat.id ? { ...chat, ...updatedChat } : chat
            )
            return updatedChats.sort((a, b) => {
              const timeA = a.last_message_time || a.created_at
              const timeB = b.last_message_time || b.created_at
              return new Date(timeB).getTime() - new Date(timeA).getTime()
            })
          })
        }
      )
      .subscribe()

    // Subscribe to message inserts and updates to update unread counts in real-time
    const messagesSubscription = supabase
      .channel('all-messages-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
        },
        (payload) => {
          const newMessage = payload.new as ChatMessage
          // Only update unread count if the message is not from the current user
          if (newMessage.sender_id !== userId) {
            setChats((prevChats) =>
              prevChats.map((chat) =>
                chat.id === newMessage.chat_id ? { ...chat, unread_count: (chat.unread_count || 0) + 1 } : chat
              )
            )
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
        },
        (payload) => {
          const updatedMessage = payload.new as ChatMessage
          const oldMessage = payload.old as ChatMessage

          // If message status changed from unread to read, decrement unread count
          if (oldMessage.status !== 'read' && updatedMessage.status === 'read' && updatedMessage.sender_id !== userId) {
            setChats((prevChats) =>
              prevChats.map((chat) =>
                chat.id === updatedMessage.chat_id
                  ? { ...chat, unread_count: Math.max((chat.unread_count || 0) - 1, 0) }
                  : chat
              )
            )
          }
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
      messagesSubscription.unsubscribe()
    }
  }, [userId])

  // Fetch messages for active chat
  useEffect(() => {
    if (!activeChatId) {
      setMessages([])
      setMessagesLoading(false)
      setHasMore(true)
      setOldestMessageId(null)
      return
    }

    const fetchMessages = async () => {
      try {
        setMessagesLoading(true)

        const { data, error: fetchError } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('chat_id', activeChatId)
          .order('created_at', { ascending: false })
          .limit(MESSAGES_PER_PAGE)

        if (fetchError) {
          throw fetchError
        }

        const reversedData = (data || []).reverse()
        setMessages(reversedData)
        setHasMore((data || []).length === MESSAGES_PER_PAGE)
        setOldestMessageId(reversedData.length > 0 ? reversedData[0].id : null)
      } catch (err) {
        console.error('Error fetching messages:', err)
        setMessagesError(err as Error)
      } finally {
        setMessagesLoading(false)
      }
    }

    fetchMessages()

    // Set up real-time subscription for new messages
    const subscription = supabase
      .channel(`chat-messages-${activeChatId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `chat_id=eq.${activeChatId}`,
        },
        (payload) => {
          const newMessage = payload.new as ChatMessage
          setMessages((prev) => [...prev, newMessage])
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `chat_id=eq.${activeChatId}`,
        },
        (payload) => {
          const updatedMessage = payload.new as ChatMessage
          setMessages((prev) => prev.map((msg) => (msg.id === updatedMessage.id ? updatedMessage : msg)))
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [activeChatId])

  const createChat = useCallback(async (athleteId: string, coachId: string): Promise<Chat | null> => {
    try {
      if (athleteId === coachId) {
        return null
      }

      const { data: existingChat } = await supabase
        .from('chats')
        .select('*')
        .eq('athlete_id', athleteId)
        .eq('coach_id', coachId)
        .single()

      if (existingChat) {
        return existingChat
      }

      const { data, error: createError } = await supabase
        .from('chats')
        .insert({
          athlete_id: athleteId,
          coach_id: coachId,
        })
        .select(`
            *,
            athlete_profile:profiles!chats_athlete_profile_fkey(first_name, last_name, avatar_url),
            coach_profile:profiles!chats_coach_profile_fkey(first_name, last_name, avatar_url)
          `)
        .single()

      if (createError) {
        throw createError
      }

      if (data) {
        return {
          ...data,
          athlete_profile: Array.isArray(data.athlete_profile) ? data.athlete_profile[0] : data.athlete_profile,
          coach_profile: Array.isArray(data.coach_profile) ? data.coach_profile[0] : data.coach_profile,
        }
      }

      return null
    } catch (err) {
      console.error('Error creating chat:', err)
      throw err
    }
  }, [])

  const createChatsForAthletes = useCallback(
    async (athleteIds: string[], coachId: string): Promise<Chat[]> => {
      const createdChats: Chat[] = []

      for (const athleteId of athleteIds) {
        try {
          const chat = await createChat(athleteId, coachId)
          if (chat) {
            createdChats.push(chat)
          }
        } catch (err) {
          console.error(`Error creating chat for athlete ${athleteId}:`, err)
        }
      }

      return createdChats
    },
    [createChat]
  )

  const sendMessage = useCallback(
    async (content: string, senderId: string, file?: File): Promise<ChatMessage | null> => {
      if (!activeChatId) {
        throw new Error('No chat selected')
      }

      try {
        let attachmentUrl: string | null = null
        let attachmentName: string | null = null
        let attachmentType: string | null = null
        let attachmentSize: number | null = null

        if (file) {
          const fileExt = file.name.split('.').pop()
          const fileName = `${activeChatId}/${Date.now()}.${fileExt}`

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('chat-attachments')
            .upload(fileName, file, {
              cacheControl: '3600',
              upsert: false,
            })

          if (uploadError) {
            throw uploadError
          }

          attachmentUrl = uploadData.path
          attachmentName = file.name
          attachmentType = file.type
          attachmentSize = file.size
        }

        const res = await supabase.functions.invoke('send-chat-message', {
          body: {
            chat_id: activeChatId,
            sender_id: senderId,
            content,
            attachment_url: attachmentUrl,
            attachment_name: attachmentName,
            attachment_type: attachmentType,
            attachment_size: attachmentSize,
          },
        })
        if (res.error) {
          throw new Error(res.error.message)
        }
        return res.data
      } catch (err) {
        console.error('Error sending message:', err)
        throw err
      }
    },
    [activeChatId]
  )

  const markAsRead = useCallback(
    async (messageIds: string[]) => {
      if (!activeChatId || messageIds.length === 0) return

      try {
        const { error: updateError } = await supabase
          .from('chat_messages')
          .update({ status: 'read' })
          .in('id', messageIds)
          .eq('chat_id', activeChatId)

        if (updateError) {
          throw updateError
        }
      } catch (err) {
        console.error('Error marking messages as read:', err)
        throw err
      }
    },
    [activeChatId]
  )

  const loadMoreMessages = useCallback(async () => {
    if (!activeChatId || !hasMore || loadingMore || !oldestMessageId) return

    try {
      setLoadingMore(true)

      const oldestMessage = messages.find((m) => m.id === oldestMessageId)
      if (!oldestMessage) return

      const { data, error: fetchError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('chat_id', activeChatId)
        .lt('created_at', oldestMessage.created_at)
        .order('created_at', { ascending: false })
        .limit(MESSAGES_PER_PAGE)

      if (fetchError) {
        throw fetchError
      }

      if (data && data.length > 0) {
        const reversedData = data.reverse()
        setMessages((prev) => [...reversedData, ...prev])
        setHasMore(data.length === MESSAGES_PER_PAGE)
        setOldestMessageId(reversedData[0].id)
      } else {
        setHasMore(false)
      }
    } catch (err) {
      console.error('Error loading more messages:', err)
      setMessagesError(err as Error)
    } finally {
      setLoadingMore(false)
    }
  }, [activeChatId, hasMore, loadingMore, oldestMessageId, messages])

  const openChatWithAthlete = useCallback(
    (athleteId: string) => {
      const athleteChat = chats.find((chat) => chat.athlete_id === athleteId)
      if (athleteChat) {
        setActiveChatId(athleteChat.id)
        setIsChatOpen(true)
      }
    },
    [chats]
  )

  const value: ChatContextValue = {
    chats,
    chatsLoading,
    chatsError,
    createChat,
    createChatsForAthletes,
    messages,
    messagesLoading,
    messagesError,
    hasMore,
    loadingMore,
    sendMessage,
    markAsRead,
    loadMoreMessages,
    activeChatId,
    setActiveChatId,
    isChatOpen,
    setIsChatOpen,
    openChatWithAthlete,
  }

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}
